/**
 * VITOMALIA HUNDELEXIKON — overview JS
 * Vanilla ES2018+. No dependencies. Defer-loaded.
 *
 * Architecture:
 *  - Liquid renders empty letter grids; JS hydrates them from a static
 *    JSON asset (`assets/vmlx-search-index.json`) that contains all 188+
 *    article entries (Shopify Liquid `paginate` is capped at 50, so we
 *    decouple data from server-render).
 *  - Live search runs on the same in-memory dataset.
 *  - Category filter shows/hides matching cards across all letters.
 *  - A-Z bar is scroll-spy + smooth-scroll based.
 *
 * The section root is `[data-vmlx-root]`.
 * The data URL is on `[data-vmlx-search-index-url]`.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-vmlx-root]');
  if (!root) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const indexHost = root.querySelector('[data-vmlx-search-index-url]');
  const indexUrl = indexHost ? indexHost.getAttribute('data-vmlx-search-index-url') : null;

  let SEARCH_INDEX = [];
  let CARDS = []; // populated after render
  let activeCats = new Set();

  /* ---------------------------------------------------------- Utils */
  function normalize(str) {
    if (!str) return '';
    return String(str)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/œ/g, 'oe')
      .replace(/æ/g, 'ae');
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, ms) {
    let t = null;
    return function () {
      const args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), ms);
    };
  }

  function highlightMatch(text, queryNorm) {
    if (!queryNorm) return escapeHtml(text);
    const safeText = escapeHtml(text);
    const lower = normalize(text);
    const idx = lower.indexOf(queryNorm);
    if (idx === -1) return safeText;
    const before = safeText.slice(0, idx);
    const match = safeText.slice(idx, idx + queryNorm.length);
    const after = safeText.slice(idx + queryNorm.length);
    return before + '<mark>' + match + '</mark>' + after;
  }

  /* ---------------------------------------------------------- Card rendering */
  function buildCardHtml(item, opts) {
    opts = opts || {};
    const url = item.url || '#';
    const title = escapeHtml(item.title || '');
    const cat = item.category || '';
    const def = item.definition_short || '';
    const catHtml = cat
      ? '<div class="vmlx-card__head"><span class="vmlx-card__cat">' + escapeHtml(cat) + '</span></div>'
      : '';
    const defHtml = def
      ? '<p class="vmlx-card__def">' + escapeHtml(def) + '</p>'
      : '';
    return '<a class="vmlx-card" href="' + escapeHtml(url) +
      '" data-vmlx-card data-vmlx-card-cat="' + escapeHtml(cat) +
      '" data-vmlx-card-letter="' + escapeHtml(item.letter || '') + '">' +
      catHtml +
      '<h3 class="vmlx-card__title">' + title + '</h3>' +
      defHtml +
      '</a>';
  }

  function hydrateLetterGrids(items) {
    // Group by letter
    const byLetter = {};
    items.forEach(it => {
      let l = (it.letter || '').toUpperCase();
      // Normalize Ü, Ö, Ä → U, O, A for grouping
      if (l === 'Ü') l = 'U';
      if (l === 'Ö') l = 'O';
      if (l === 'Ä') l = 'A';
      if (!l) return;
      if (!byLetter[l]) byLetter[l] = [];
      byLetter[l].push(it);
    });

    // Sort each letter's articles alphabetically
    Object.values(byLetter).forEach(arr => {
      arr.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'de'));
    });

    // Populate each letter grid
    const grids = root.querySelectorAll('[data-vmlx-letter-grid]');
    grids.forEach(grid => {
      const letter = grid.getAttribute('data-vmlx-letter-grid');
      const arr = byLetter[letter] || [];
      const section = grid.closest('[data-vmlx-letter-section]');
      const countEl = section ? section.querySelector('[data-vmlx-letter-count]') : null;

      if (arr.length === 0) {
        // No articles for this letter (shouldn't happen given Liquid pre-filters letters)
        grid.innerHTML = '';
        if (countEl) countEl.textContent = '';
        if (section) section.style.display = 'none';
        return;
      }

      grid.innerHTML = arr.map(it => buildCardHtml(it)).join('');
      if (countEl) {
        countEl.textContent = arr.length === 1 ? '1 Begriff' : arr.length + ' Begriffe';
      }
    });

    // Refresh CARDS reference
    CARDS = Array.from(root.querySelectorAll('[data-vmlx-card]'));
    applyFilter();
  }

  /* ---------------------------------------------------------- Filter */
  function readCatsFromUrl() {
    const sp = new URLSearchParams(window.location.search);
    const raw = sp.get('kat');
    if (!raw) return new Set();
    return new Set(raw.split(',').map(s => decodeURIComponent(s.trim())).filter(Boolean));
  }

  function writeCatsToUrl() {
    const sp = new URLSearchParams(window.location.search);
    if (activeCats.size === 0) sp.delete('kat');
    else sp.set('kat', Array.from(activeCats).join(','));
    const newUrl = window.location.pathname + (sp.toString() ? '?' + sp.toString() : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }

  function applyFilter() {
    const filterActive = activeCats.size > 0;
    let visibleCount = 0;

    CARDS.forEach(card => {
      const cardCat = card.getAttribute('data-vmlx-card-cat') || '';
      const matches = !filterActive || activeCats.has(cardCat);
      if (matches) {
        card.removeAttribute('hidden');
        visibleCount++;
      } else {
        card.setAttribute('hidden', '');
      }
    });

    // Hide letter sections that are empty after filter
    const letterSections = root.querySelectorAll('[data-vmlx-letter-section]');
    letterSections.forEach(ls => {
      const visible = ls.querySelectorAll('[data-vmlx-card]:not([hidden])');
      if (visible.length === 0) {
        ls.setAttribute('data-letter-empty', 'true');
      } else {
        ls.setAttribute('data-letter-empty', 'false');
      }
    });

    // Update alphabar dim state
    const alphaLinks = root.querySelectorAll('[data-vmlx-alphalink]');
    alphaLinks.forEach(link => {
      const letter = link.getAttribute('data-vmlx-alphalink');
      const target = root.querySelector('[data-vmlx-letter-section="' + letter + '"]');
      if (!target) return;
      if (target.getAttribute('data-letter-empty') === 'true') {
        link.setAttribute('aria-disabled', 'true');
      } else {
        link.removeAttribute('aria-disabled');
      }
    });

    // Reset button visibility
    const resetBtn = root.querySelector('[data-vmlx-reset]');
    if (resetBtn) {
      if (filterActive) resetBtn.removeAttribute('hidden');
      else resetBtn.setAttribute('hidden', '');
    }

    // Empty state (only when search is also empty)
    const emptyEl = root.querySelector('[data-vmlx-empty]');
    if (emptyEl) {
      if (visibleCount === 0 && filterActive) {
        emptyEl.setAttribute('data-active', 'true');
      } else {
        emptyEl.removeAttribute('data-active');
      }
    }
  }

  function updatePillStates() {
    root.querySelectorAll('[data-vmlx-cat]').forEach(p => {
      const cat = p.getAttribute('data-vmlx-cat');
      p.setAttribute('aria-pressed', activeCats.has(cat) ? 'true' : 'false');
    });
  }

  function toggleCat(cat) {
    if (activeCats.has(cat)) activeCats.delete(cat);
    else activeCats.add(cat);
    updatePillStates();
    writeCatsToUrl();
    applyFilter();
  }

  function resetFilter() {
    activeCats.clear();
    updatePillStates();
    writeCatsToUrl();
    applyFilter();
  }

  /* ---------------------------------------------------------- Search */
  function setupSearch(container) {
    const input = container.querySelector('[data-vmlx-search-input]');
    const resultsBox = container.querySelector('[data-vmlx-search-results]');
    if (!input || !resultsBox) return;

    let activeIdx = -1;

    function render(query) {
      const q = normalize(query.trim());
      if (q.length < 2) {
        container.removeAttribute('data-open');
        resultsBox.innerHTML = '';
        activeIdx = -1;
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      const results = searchIndex(q);
      activeIdx = -1;
      if (results.length === 0) {
        resultsBox.innerHTML = '<div class="vmlx-search__empty">Keine Treffer für „' + escapeHtml(query) + '"</div>';
      } else {
        resultsBox.innerHTML = results.map((r, i) => {
          const titleHtml = highlightMatch(r.title, q);
          const defHtml = r.definition_short ? '<span class="vmlx-search__result-def">' + highlightMatch(r.definition_short, q) + '</span>' : '';
          const catHtml = r.category ? '<span class="vmlx-search__result-cat">' + escapeHtml(r.category) + '</span>' : '';
          return '<a class="vmlx-search__result" role="option" id="vmlx-r-' + i + '" href="' + escapeHtml(r.url) + '">' +
                 '<span><span class="vmlx-search__result-title">' + titleHtml + '</span>' + defHtml + '</span>' +
                 catHtml + '</a>';
        }).join('');
      }
      container.setAttribute('data-open', 'true');
      input.setAttribute('aria-expanded', 'true');
    }

    function searchIndex(qNorm) {
      const titleHits = [];
      const defHits = [];
      for (let i = 0; i < SEARCH_INDEX.length; i++) {
        const item = SEARCH_INDEX[i];
        const tNorm = normalize(item.title);
        if (tNorm.includes(qNorm)) {
          titleHits.push({ item: item, pos: tNorm.indexOf(qNorm) });
          continue;
        }
        const dNorm = normalize(item.definition_short || '');
        if (dNorm.includes(qNorm)) {
          defHits.push({ item: item, pos: dNorm.indexOf(qNorm) });
        }
      }
      titleHits.sort((a, b) => a.pos - b.pos);
      defHits.sort((a, b) => a.pos - b.pos);
      return titleHits.concat(defHits).slice(0, 8).map(h => h.item);
    }

    function close() {
      container.removeAttribute('data-open');
      input.setAttribute('aria-expanded', 'false');
      activeIdx = -1;
    }

    function setActive(newIdx) {
      const items = resultsBox.querySelectorAll('.vmlx-search__result');
      if (items.length === 0) return;
      if (activeIdx >= 0 && items[activeIdx]) items[activeIdx].removeAttribute('aria-selected');
      activeIdx = ((newIdx % items.length) + items.length) % items.length;
      const cur = items[activeIdx];
      cur.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', cur.id);
      cur.scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('input', debounce(() => render(input.value), 80));

    input.addEventListener('keydown', e => {
      const items = resultsBox.querySelectorAll('.vmlx-search__result');
      if (e.key === 'ArrowDown') { e.preventDefault(); if (items.length) setActive(activeIdx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (items.length) setActive(activeIdx - 1); }
      else if (e.key === 'Enter') {
        if (activeIdx >= 0 && items[activeIdx]) { e.preventDefault(); window.location.href = items[activeIdx].getAttribute('href'); }
      } else if (e.key === 'Escape') { close(); input.blur(); }
    });

    input.addEventListener('focus', () => { if (input.value.trim().length >= 2) render(input.value); });
    document.addEventListener('click', e => { if (!container.contains(e.target)) close(); });
  }

  /* ---------------------------------------------------------- Wire UI */
  function wireFilterPills() {
    root.querySelectorAll('[data-vmlx-cat]').forEach(p => {
      p.addEventListener('click', e => {
        e.preventDefault();
        const cat = p.getAttribute('data-vmlx-cat');
        if (cat) toggleCat(cat);
      });
    });

    const moreToggle = root.querySelector('[data-vmlx-more-toggle]');
    if (moreToggle) {
      const wrapper = moreToggle.parentElement;
      moreToggle.addEventListener('click', e => {
        e.preventDefault();
        const open = wrapper.getAttribute('data-open') === 'true';
        wrapper.setAttribute('data-open', open ? 'false' : 'true');
        moreToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
      document.addEventListener('click', e => {
        if (!wrapper.contains(e.target)) {
          wrapper.setAttribute('data-open', 'false');
          moreToggle.setAttribute('aria-expanded', 'false');
        }
      });
    }

    const resetBtn = root.querySelector('[data-vmlx-reset]');
    if (resetBtn) resetBtn.addEventListener('click', e => { e.preventDefault(); resetFilter(); });

    const emptyBtn = root.querySelector('[data-vmlx-empty-reset]');
    if (emptyBtn) {
      emptyBtn.addEventListener('click', e => {
        e.preventDefault();
        root.querySelectorAll('[data-vmlx-search-input]').forEach(i => { i.value = ''; });
        resetFilter();
      });
    }
  }

  function wireAlphabar() {
    const alphaLinks = Array.from(root.querySelectorAll('[data-vmlx-alphalink]'));
    alphaLinks.forEach(link => {
      link.addEventListener('click', e => {
        const letter = link.getAttribute('data-vmlx-alphalink');
        const target = root.querySelector('[data-vmlx-letter-section="' + letter + '"]');
        if (!target) return;
        if (link.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        e.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        try { window.history.replaceState({}, '', '#vmlx-letter-' + letter.toLowerCase()); } catch (_) {}
      });
    });

    const letterSections = Array.from(root.querySelectorAll('[data-vmlx-letter-section]'));
    if ('IntersectionObserver' in window && letterSections.length > 0) {
      const obs = new IntersectionObserver(entries => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length === 0) return;
        const letter = visible[0].target.getAttribute('data-vmlx-letter-section');
        alphaLinks.forEach(l => {
          if (l.getAttribute('data-vmlx-alphalink') === letter) l.classList.add('vmlx-alphalink--active');
          else l.classList.remove('vmlx-alphalink--active');
        });
      }, { rootMargin: '-20% 0px -75% 0px', threshold: 0 });
      letterSections.forEach(ls => obs.observe(ls));
    }
  }

  function setStickyOffset() {
    try {
      const headerEl = document.querySelector('[is="sticky-header"]') || document.querySelector('header.header');
      if (headerEl) {
        const h = headerEl.getBoundingClientRect().height;
        root.style.setProperty('--vmlx-sticky-offset', Math.round(h) + 'px');
      }
    } catch (_) {}
  }

  /* ---------------------------------------------------------- Init */
  function init() {
    activeCats = readCatsFromUrl();
    wireFilterPills();
    wireAlphabar();
    setStickyOffset();
    root.querySelectorAll('[data-vmlx-search]').forEach(setupSearch);
  }

  function loadIndex() {
    if (!indexUrl) return;
    fetch(indexUrl, { credentials: 'omit' })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        SEARCH_INDEX = (data && Array.isArray(data.articles)) ? data.articles : [];
        hydrateLetterGrids(SEARCH_INDEX);
        updatePillStates();
      })
      .catch(err => {
        // On fetch failure, leave native /tagged/ links from <noscript> intact via fallback in CSS — but those are inside <noscript>
        // Alternative fallback: write a single tile per letter with link to /tagged/<letter>
        console.warn('[vmlx] Failed to load search index:', err);
        const grids = root.querySelectorAll('[data-vmlx-letter-grid]');
        grids.forEach(grid => {
          const letter = grid.getAttribute('data-vmlx-letter-grid');
          grid.innerHTML = '<a class="vmlx-card" href="/blogs/hundelexikon/tagged/' + encodeURIComponent(letter) + '">' +
            '<h3 class="vmlx-card__title">Alle Begriffe mit „' + escapeHtml(letter) + '" ansehen</h3>' +
            '<span class="vmlx-card__more">Zur Übersicht →</span></a>';
        });
        CARDS = Array.from(root.querySelectorAll('[data-vmlx-card]'));
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); loadIndex(); });
  } else {
    init();
    loadIndex();
  }
})();
